import React, { useEffect, useState } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import {
  FaSave,
  FaBuilding,
  FaPhone,
  FaEnvelope,
  FaWhatsapp,
  FaMapMarkerAlt,
  FaGlobe,
  FaInstagram,
  FaLinkedinIn,
  FaFacebook,
  FaYoutube,
  FaCheckCircle,
  FaExclamationCircle,
} from "react-icons/fa";

import { db } from "../../../firebase/firebase";

import "./Settings.css";

const Settings = () => {
  const [settings, setSettings] = useState({
    companyName: "Quality Sports Surface",
    phone: "",
    email: "",
    whatsapp: "",
    address: "",
    website: "",
    instagram: "",
    facebook: "",
    linkedin: "",
    youtube: "",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [message, setMessage] = useState({
    type: "",
    text: "",
  });

  // =====================================================
  // LOAD SETTINGS
  // =====================================================

  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);

        const settingsRef = doc(db, "settings", "website");

        const snapshot = await getDoc(settingsRef);

        if (snapshot.exists()) {
          setSettings((previous) => ({
            ...previous,
            ...snapshot.data(),
          }));
        }
      } catch (error) {
        console.error("Settings loading error:", error);

        setMessage({
          type: "error",
          text: "Failed to load website settings.",
        });
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  // =====================================================
  // INPUT CHANGE
  // =====================================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setSettings((previous) => ({
      ...previous,
      [name]: value,
    }));

    setMessage({
      type: "",
      text: "",
    });
  };

  // =====================================================
  // SAVE SETTINGS
  // =====================================================

  const handleSave = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);

      setMessage({
        type: "",
        text: "",
      });

      const settingsRef = doc(db, "settings", "website");

      await setDoc(
        settingsRef,
        {
          ...settings,
          updatedAt: new Date(),
        },
        {
          merge: true,
        },
      );

      setMessage({
        type: "success",
        text: "Website settings saved successfully.",
      });
    } catch (error) {
      console.error("Settings save error:", error);

      setMessage({
        type: "error",
        text: error.message || "Failed to save settings.",
      });
    } finally {
      setSaving(false);
    }
  };

  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {
    return (
      <div className="settings-loading">
        <div className="settings-spinner"></div>

        <p>Loading website settings...</p>
      </div>
    );
  }

  // =====================================================
  // PAGE
  // =====================================================

  return (
    <div className="settings-page">
      {/* HEADER */}

      <div className="settings-header">
        <div>
          <h2>Website Settings</h2>

          <p>Manage your company information and website contact details.</p>
        </div>
      </div>

      {/* MESSAGE */}

      {message.text && (
        <div
          className={`settings-message ${
            message.type === "success" ? "success" : "error"
          }`}
        >
          {message.type === "success" ? (
            <FaCheckCircle />
          ) : (
            <FaExclamationCircle />
          )}

          <span>{message.text}</span>
        </div>
      )}

      {/* FORM */}

      <form className="settings-form" onSubmit={handleSave}>
        {/* =================================================
            COMPANY INFORMATION
        ================================================= */}

        <div className="settings-card">
          <div className="settings-card-header">
            <div className="settings-section-icon">
              <FaBuilding />
            </div>

            <div>
              <h3>Company Information</h3>

              <p>Basic information about your company.</p>
            </div>
          </div>

          <div className="settings-grid">
            <div className="settings-field full">
              <label>Company Name</label>

              <div className="settings-input">
                <FaBuilding />

                <input
                  type="text"
                  name="companyName"
                  value={settings.companyName}
                  onChange={handleChange}
                  placeholder="Quality Sports Surface"
                />
              </div>
            </div>

            <div className="settings-field">
              <label>Phone Number</label>

              <div className="settings-input">
                <FaPhone />

                <input
                  type="tel"
                  name="phone"
                  value={settings.phone}
                  onChange={handleChange}
                  placeholder="+91 XXXXX XXXXX"
                />
              </div>
            </div>

            <div className="settings-field">
              <label>Email Address</label>

              <div className="settings-input">
                <FaEnvelope />

                <input
                  type="email"
                  name="email"
                  value={settings.email}
                  onChange={handleChange}
                  placeholder="info@example.com"
                />
              </div>
            </div>

            <div className="settings-field">
              <label>WhatsApp Number</label>

              <div className="settings-input">
                <FaWhatsapp />

                <input
                  type="tel"
                  name="whatsapp"
                  value={settings.whatsapp}
                  onChange={handleChange}
                  placeholder="+91 XXXXX XXXXX"
                />
              </div>
            </div>

            <div className="settings-field">
              <label>Website</label>

              <div className="settings-input">
                <FaGlobe />

                <input
                  type="url"
                  name="website"
                  value={settings.website}
                  onChange={handleChange}
                  placeholder="https://example.com"
                />
              </div>
            </div>

            <div className="settings-field full">
              <label>Address</label>

              <div className="settings-input">
                <FaMapMarkerAlt />

                <textarea
                  name="address"
                  value={settings.address}
                  onChange={handleChange}
                  placeholder="Enter company address"
                  rows="4"
                />
              </div>
            </div>
          </div>
        </div>

        {/* =================================================
            SOCIAL MEDIA
        ================================================= */}

        <div className="settings-card">
          <div className="settings-card-header">
            <div className="settings-section-icon social">
              <FaGlobe />
            </div>

            <div>
              <h3>Social Media</h3>

              <p>Add your company's social media links.</p>
            </div>
          </div>

          <div className="settings-grid">
            <div className="settings-field">
              <label>Instagram</label>

              <div className="settings-input">
                <FaInstagram />

                <input
                  type="url"
                  name="instagram"
                  value={settings.instagram}
                  onChange={handleChange}
                  placeholder="https://instagram.com/..."
                />
              </div>
            </div>

            <div className="settings-field">
              <label>Facebook</label>

              <div className="settings-input">
                <FaFacebook />

                <input
                  type="url"
                  name="facebook"
                  value={settings.facebook}
                  onChange={handleChange}
                  placeholder="https://facebook.com/..."
                />
              </div>
            </div>

            <div className="settings-field">
              <label>YouTube</label>

              <div className="settings-input">
                <FaYoutube />

                <input
                  type="url"
                  name="youtube"
                  value={settings.youtube}
                  onChange={handleChange}
                  placeholder="https://youtube.com/..."
                />
              </div>
            </div>
          </div>
        </div>

        <div className="settings-field">
          <label>Linkedin</label>

          <div className="settings-input">
            <FaLinkedinIn />

            <input
              type="url"
              name="linkedin"
              value={settings.linkedin}
              onChange={handleChange}
              placeholder="https://linkedin.com/..."
            />
          </div>
        </div>
        {/* =================================================
            SAVE
        ================================================= */}

        <div className="settings-actions">
          <button
            type="submit"
            className="settings-save-button"
            disabled={saving}
          >
            <FaSave />

            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Settings;
